// [목적] Sidebar "새 컬렉션" 버튼 → 제목만 입력받아 컬렉션 생성(POST /collections)하는 모달
// [이유] G1-4: Sidebar.tsx:443-463 "새 컬렉션" 버튼이 표시만 되고 클릭 핸들러가 없던 죽은 UI를
//        실제 collection.client.create() 호출로 배선한다. 컬렉션 CRUD 전용 페이지가 아직 없어
//        title(필수)만 받는 최소 폼으로 시작(summary/coverUrl 은 후속 CollectionDetail 편집에서 채움 —
//        createCollectionInputSchema 가 옵션 처리하므로 생략 가능, ponytail).
// [사이드 이팩트] 성공 시 queryKeys.collection.list 전체 캐시 무효화(Sidebar 사이드바 목록 갱신) +
//                /collections/:id 로 이동 + Toast 성공 알림. 실패 시 Toast 에러 알림(모달 유지).
// [수정 시 고려사항] MUI Modal 은 Esc 로 기본 닫힘 — 별도 keydown 핸들러 불필요.
//                   visibility 는 BE 기본값 'public' 그대로 사용(폼에 노출 안 함, 최소 스코프).

import { useState, type FormEvent } from 'react';
import { Box, Button, Modal, TextField, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import * as collectionClient from '../../api/collection.client.js';
import { ApiError } from '../../api/http.js';
import { useToast } from '../feedback/Toast.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const BORDER = c.line.base[L];
const BRAND = c.brand[500][L];

export interface NewCollectionModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * 생성 요청이 401(미인증·세션 만료)로 실패했을 때 호출 — 호출자가 로그인 유도를 띄운다.
   * 미전달이면 다른 실패와 동일하게 일반 오류 토스트만 표시한다.
   */
  onAuthRequired?: () => void;
}

/**
 * [목적] 새 컬렉션 생성 모달 — 제목 입력 + 생성 버튼
 * [이유] G1-4: Sidebar "새 컬렉션" CTA 실배선.
 * [사이드 이팩트] 성공 시 onClose() 호출 + /collections/:id 로 navigate.
 * [수정 시 고려사항] title 공백/빈 문자열이면 제출 버튼 비활성(BE min(1) 과 정합).
 *                   [QA-P2-01] 401 은 "실패했습니다" 토스트 대신 onAuthRequired 로 로그인 유도 — 게스트가 Sidebar 가드를
 *                   통과하지 못하는 것이 1차 방어이고, 세션이 열려 있는 동안 만료된 경우의 2차 방어다.
 */
export function NewCollectionModal({
  open,
  onClose,
  onAuthRequired,
}: NewCollectionModalProps): JSX.Element {
  const [title, setTitle] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();

  const mutation = useMutation({
    mutationFn: () => collectionClient.create({ title: title.trim(), visibility: 'public' }),
    onSuccess: (created) => {
      // [목적] Sidebar 사이드바 컬렉션 목록(및 향후 CollectionListPage) 캐시 갱신
      // [이유] queryKeys.ts 주석 관례: "invalidateQueries({queryKey:['collection']})로 전체 캐시 무효화".
      void queryClient.invalidateQueries({ queryKey: ['collection'] });
      show('컬렉션을 만들었습니다.', 'success');
      setTitle('');
      onClose();
      navigate(`/collections/${created.id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401 && onAuthRequired) {
        // 로그인이 필요한 상태 — 재시도해도 소용없는 "실패" 토스트 대신 로그인 유도(입력한 제목은 지운다)
        setTitle('');
        onClose();
        onAuthRequired();
        return;
      }
      show('컬렉션 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.', 'error');
    },
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!title.trim() || mutation.isPending) return;
    mutation.mutate();
  };

  const handleClose = (): void => {
    if (mutation.isPending) return;
    setTitle('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="new-collection-modal-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '16px' }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          bgcolor: c.paper[0][L],
          borderRadius: `${tokens.radius.l}px`,
          width: '100%',
          maxWidth: 400,
          p: '24px',
          boxShadow: tokens.shadow.modal,
          outline: 'none',
        }}
      >
        <Typography
          id="new-collection-modal-title"
          sx={{ fontSize: 15, fontWeight: 800, color: INK_HI, mb: '16px' }}
        >
          새 컬렉션
        </Typography>
        <TextField
          autoFocus
          label="컬렉션 제목"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          inputProps={{ maxLength: 80 }}
          sx={{ mb: '20px' }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button
            onClick={handleClose}
            disabled={mutation.isPending}
            sx={{
              color: INK_MID,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              textTransform: 'none',
            }}
          >
            취소
          </Button>
          <Button
            type="submit"
            disabled={!title.trim() || mutation.isPending}
            variant="contained"
            sx={{
              bgcolor: BRAND,
              borderRadius: `${tokens.radius.m}px`,
              textTransform: 'none',
              '&:hover': { bgcolor: BRAND },
              // [수정: 디자인 개편] 비활성일 때도 파란 배경이 그대로 남아 누를 수 있을 것처럼 보였다 — 회색 면으로 명시
              '&.Mui-disabled': { bgcolor: c.paper[2][L], color: c.ink.low[L] },
            }}
          >
            {mutation.isPending ? '만드는 중…' : '만들기'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
